import React, { useEffect, useState } from 'react';
import { Modal, Button, Alert, Form, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { fetchHelper } from '../../../helpers/fetchHelper';

interface StatusRes { isEnabled: boolean }
interface SetupRes  { secret: string; otpauthUri: string }
interface VerifyReq { code: string }
interface VerifyRes { recoveryCodes: string[] }

const TwoFactorSection: React.FC = () => {
    const [isEnabled, setIsEnabled]   = useState<boolean | null>(null);
    const [showModal, setShowModal]   = useState(false);
    const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
    const [verifyCode, setVerifyCode] = useState('');
    const [recovery, setRecovery]     = useState<string[] | null>(null);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            const response = await fetchHelper.getJson<StatusRes>('/api/2fa/status');
            if (response && response.data && response.status >= 200 && response.status < 300) {
                setIsEnabled(response.data.isEnabled);
            } else {
                console.error('Error fetching 2FA status or unexpected response structure:', response);
            }
        })();
    }, []);

    const beginEnable = async () => {
        setLoading(true); setError(null);
        try {
            const response = await fetchHelper.getJson<SetupRes>('/api/2fa/setup');
            if (response && response.data && response.data.otpauthUri && response.status >= 200 && response.status < 300) {
                setOtpauthUri(response.data.otpauthUri);
                setShowModal(true);
            } else {
                setError('Failed to start 2FA setup. Please try again.');
            }
        } catch (e: any) {
            setError('An unexpected error occurred during 2FA setup. Please try again.');
        } finally { setLoading(false); }
    };

    const confirmEnable = async () => {
        if (verifyCode.length !== 6) return;
        setLoading(true); setError(null);
        try {
            const body: VerifyReq = { code: verifyCode };
            const response = await fetchHelper.postJson<VerifyRes, VerifyReq>('/api/2fa/verify', body);

            if (response && response.data && Array.isArray(response.data.recoveryCodes) && response.status >= 200 && response.status < 300) {
                const { recoveryCodes } = response.data;
                setShowModal(false);
                setRecovery(recoveryCodes);
                setIsEnabled(true);
            } else {
                setError('Invalid verification code. Please try again.');
            }
        } catch (e: any) {
            setError('An unexpected error occurred during verification. Please try again.');
        } finally { setLoading(false); }
    };

    const disable = async () => {
        if (!window.confirm('Really disable 2-factor authentication?')) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchHelper.postJson('/api/2fa/disable', {});
            if (response && response.status >= 200 && response.status < 300) {
                setIsEnabled(false);
                setRecovery(null);
            } else {
                setError('Failed to disable 2FA. Please try again.');
            }
        } catch (e: any) {
            setError('An unexpected error occurred while disabling 2FA. Please try again.');
        } finally { setLoading(false); }
    };

    const StatusBadge = () => (
        <Alert variant={isEnabled ? 'success' : 'danger'}>
            Currently two-factor authentication is  {isEnabled ? 'enabled' : 'disabled'} on your account.
        </Alert>
    );

    return (
        <div className="bg-light p-30 pb-40 brad-10 b-1 w-100 center-block mb-20 ">
            <div className='text-center'>
            <h4 className="pb-20 text-400">
                Two-factor authentication 
            </h4>
            <p>
                Two-factor authentication (2FA) is optional, but highly recommended for improving the security of your account. It adds an extra layer of protection - in addition to your password, you'll be asked to enter a unique code each time you try to log in to this website. This code is generated by an authentication app on your phone. We recommend using the free Google Authenticator app (<a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener">for iOS</a> or <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_GB&pli=1" target="_blank" rel="noopener">Android</a>). However, you can use any authentication app of your choice.
            </p>
            <p>
                <StatusBadge />
            </p>
            {isEnabled
                ? <Button variant="danger" onClick={disable} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : 'Disable 2FA'}
                </Button>
                : <Button variant="cta" onClick={beginEnable} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : 'Enable 2FA'}
                </Button>}

            {error && <p className="text-danger mt-2">{error}</p>}
            </div>

            

            {/* recovery codes ― shown once */}
            {/*{recovery && (*/}
            {/*    <div className="mt-4">*/}
            {/*        <h5>Save these one-time recovery codes!</h5>*/}
            {/*        <pre className="bg-light p-3">{recovery.join('\n')}</pre>*/}
            {/*        <Button onClick={() => setRecovery(null)}>Got it</Button>*/}
            {/*    </div>*/}
            {/*)}*/}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Finish enabling 2FA</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {otpauthUri && <QRCodeSVG value={otpauthUri} size={200} />}
                    <p className="mt-3">
                        Scan the QR code with Google Authenticator, then enter the 6-digit code below.
                    </p>
                    <Form.Control
                        type="text"
                        value={verifyCode}
                        maxLength={6}
                        onChange={e => setVerifyCode(e.target.value)}
                        className="text-center fw-bold text-lg"
                        placeholder="123 456"
                        autoFocus
                    />
                    {error && <p className="text-danger mt-2">{error}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="cta"
                        onClick={confirmEnable}
                        disabled={loading || verifyCode.length !== 6}
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : 'Verify & Enable'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TwoFactorSection;
